import { z } from 'zod';
import { ErrorCode } from './errors.js';
/**
 * Sổ lỗi AI — APM-H-m (`plan-harness.md` §9.2). Mỗi mã `AI_*` khai ba điều:
 *
 * - `stage`: khối nào của harness hỏng (Context → LLM → Policy → Runtime →
 *   Verify → Result), để trang Harness tô đúng cột và chỉ số "lỗi theo stage"
 *   không phải đoán từ message.
 * - `userMessage`: câu cho người dùng cuối — không có chi tiết kỹ thuật.
 * - `operatorHint`: người vận hành đọc bước nào trong dấu vết, nguyên nhân
 *   thường gặp, cách sửa.
 *
 * `docs/ai-errors.md` **sinh từ đây** (`pnpm ai:errors:doc`); test contracts
 * khẳng định không mã `AI_*` nào thiếu hàng — thêm mã mà quên viết hướng dẫn
 * là test đỏ.
 */
export const AiRunStage = z.enum(['context', 'llm', 'policy', 'runtime', 'verify', 'result']);
export const AI_ERROR_CATALOG = {
    AI_NOT_CONFIGURED: {
        stage: 'policy',
        userMessage: 'Trợ lý AI chưa được cấu hình. Bạn vẫn làm tay được theo hướng dẫn kèm bên.',
        operatorHint: 'Chưa có key AI cho người này, hoặc key chưa xác minh. Xem `GET /ai/capabilities.reason`; key thuộc **người**, không thuộc tổ chức (APM-805).',
    },
    AI_BUDGET_EXCEEDED: {
        stage: 'policy',
        userMessage: 'Hạn mức chi tiêu AI của kỳ này đã hết.',
        operatorHint: 'Xem `ai_budget` và `ai_usage_daily`. Nới hạn mức là quyết định có dấu vết; đừng xoá log để "reset".',
    },
    AI_RATE_LIMITED: {
        stage: 'policy',
        userMessage: 'Bạn gọi trợ lý AI quá nhanh. Hãy chờ một lát rồi thử lại.',
        operatorHint: 'Bước `policy.rateLimit` của run trước cho thấy đã dùng bao nhiêu. Trần ở `ai.policy.rate_*`; chưa tốn token nào.',
    },
    AI_SKILL_DISABLED: {
        stage: 'policy',
        userMessage: 'Tính năng AI này đã được quản trị viên tắt.',
        operatorHint: 'Khoá `ai.policy.disabled_skills`. Đường làm tay vẫn hiện; nếu người dùng không thấy, lỗi ở `manualFallbacks`.',
    },
    AI_CONTEXT_OVERFLOW: {
        stage: 'context',
        userMessage: 'Yêu cầu quá dài để trợ lý xử lý trong một lượt. Hãy thu hẹp phạm vi.',
        operatorHint: 'Prompt hệ thống (kể cả bản ghi đè và kiến thức) vượt trần token trước khi gọi model. Xem `policy.systemHash` và bản prompt đang hoạt động; cắt lớp kiến thức theo tag.',
    },
    AI_PROVIDER_TIMEOUT: {
        stage: 'llm',
        userMessage: 'Nhà cung cấp AI phản hồi quá chậm. Hãy thử lại sau.',
        operatorHint: 'Kết nối tới provider quá hạn — không có bước `usage` cho lượt này. Thường là mạng hoặc model reasoning quá chậm; xem `stream_chunk_timeout` phía gateway.',
    },
    AI_PROVIDER_RATE_LIMITED: {
        stage: 'llm',
        userMessage: 'Nhà cung cấp AI đang quá tải. Hãy thử lại sau ít phút.',
        operatorHint: 'Provider trả 429. Nhiều run cùng lúc từ một tổ chức; xem `ai.policy.rate_*` của ta có quá rộng không.',
    },
    AI_MODEL_UNAVAILABLE: {
        stage: 'llm',
        userMessage: 'Model AI đang không sẵn dùng. Hãy thử lại sau.',
        operatorHint: 'Model id không tồn tại (404 / "model not found") hoặc provider báo overloaded. Kiểm chuỗi model `ai.model.*` và bảng năng lực; chuỗi dự phòng đã rơi hết.',
    },
    AI_PROVIDER_ERROR: {
        stage: 'llm',
        userMessage: 'Trợ lý AI gặp lỗi. Hãy thử lại.',
        operatorHint: 'Lỗi provider **chưa phân loại** — đọc log theo `runId`. Nếu lặp lại cùng hình dạng, thêm một mã riêng thay vì để nó ở đây.',
    },
    AI_REFUSED: {
        stage: 'llm',
        userMessage: 'Trợ lý AI từ chối xử lý yêu cầu này.',
        operatorHint: '`stop_reason: refusal` — không retry. Xem bước `response`/`prompt` (debug) để biết yêu cầu có gì đáng ngại; token vẫn bị tính.',
    },
    AI_TOOL_TERMINAL: {
        stage: 'runtime',
        userMessage: 'Trợ lý AI không đọc được dữ liệu cần thiết lúc này. Hãy thử lại sau.',
        operatorHint: 'Mọi tool của skill đều đã bị rút vì lỗi hạ tầng (`tool_result.error = terminal`) mà model vẫn cần gọi tool. Đọc log theo `logRef` trên bước `tool_result`; đây là lỗi của **ta** (DB, migration), không phải của model.',
    },
    AI_RUN_LIMIT: {
        stage: 'runtime',
        userMessage: 'Trợ lý AI dừng vì chạm giới hạn của lượt chạy. Kết quả (nếu có) chỉ là một phần.',
        operatorHint: 'Bước `limit` nói trần nào (`turns`, `tool_calls`, `input_tokens`, `cost`, `loop`); `usage` từng lượt cho thấy context phình hay model gọi lặp. Đừng nới trần trước khi hiểu vì sao.',
    },
    AI_OUTPUT_INVALID: {
        stage: 'verify',
        userMessage: 'Trợ lý AI không đưa ra được kết quả đúng định dạng sau nhiều lần sửa.',
        operatorHint: 'Bước `verify` cuối: `issues[].path` là trường hỏng. Thường là schema đổi mà prompt chưa, hoặc model nhỏ không theo schema lồng sâu. Sửa prompt lớp skill hoặc nâng tầng model.',
    },
    AI_PROMPT_REJECTED: {
        stage: 'context',
        userMessage: 'Bản prompt này không được chấp nhận.',
        operatorHint: '`details.blockers[].path`: thiếu `{{dynamic}}` cho skill có `prepare()`, hoặc giả thẻ `du_lieu_*`. Không phải lỗi lúc chạy.',
    },
    AI_GOLDEN_FAILED: {
        stage: 'verify',
        userMessage: 'Bản prompt này chưa qua bộ kiểm chuẩn của skill nên chưa được kích hoạt.',
        operatorHint: '`details.cases[]`: case nào đỏ và khẳng định nào hỏng (kèm `runId` để soi dấu vết sandbox). Sửa prompt rồi thử lại; ép qua bằng `force.reason` thì lý do ghi vào `forced_reason` của phiên bản.',
    },
    AI_MEASUREMENT_LEAK: {
        stage: 'context',
        userMessage: 'Nội dung này chứa chữ của bộ kiểm chuẩn (tên ca, khẳng định, hay từ như "golden") nên không được lưu vào thứ trợ lý AI sẽ đọc.',
        operatorHint: '`details.leaks[]`: `path` là trường (systemPrompt / userTemplate / body / title / text), `kind` là loại (`case` = tên ca golden, `assertion` = nguyên văn khẳng định, `term` = từ trong `MEASUREMENT_LEAK_TERMS`), `match` là chuỗi bắt được. Viết lại bài học bằng lời nghiệp vụ, không chép khẳng định của golden set — prompt học cách qua bài thi thì không còn đo được (APM-H-an). Danh sách từ là đóng, ở contracts.',
    },
    AI_PROMPT_NO_EVIDENCE: {
        stage: 'context',
        userMessage: 'Bản prompt này chưa có bản ghi bằng chứng (dấu hiệu, bài học, lượt chạy thử đã cho thấy) nên chưa được kích hoạt.',
        operatorHint: '`details.missing[]` là trường thiếu / sai schema trong `experience` (`cue`, `lesson`, `evidence.runIds`, `confidence`, `scope`); `details.runIds[]` là id không phải run **sandbox** của đúng skill trong tổ chức này. Chạy thử bản nháp ở trang Harness (mode sandbox) rồi ghi run id vào bằng chứng; `force.reason` chỉ bỏ qua golden, không bỏ qua bằng chứng (APM-H-ao).',
    },
    AI_RUN_ABANDONED: {
        stage: 'result',
        userMessage: 'Lượt chạy AI bị gián đoạn. Bạn có thể cho nó chạy tiếp.',
        operatorHint: 'Không có bước mới quá lâu — thường vì API restart giữa run. Job `ai.reap-stuck-runs` đánh dấu; không cần sửa gì trừ khi lặp lại, khi đó xem lý do restart.',
    },
    AI_RUN_NOT_RESUMABLE: {
        stage: 'result',
        userMessage: 'Lượt chạy này không chạy tiếp được — hãy chạy lại từ đầu.',
        operatorHint: 'Không có lượt hội thoại nào trọn vẹn để nối tiếp: run hỏng ngay lượt đầu, hoặc dấu vết ghi trước APM-UX-ai3 nên thiếu `tool_result.useId`. Xem `buildResumeState`.',
    },
    AI_RESUME_MODEL_CHANGED: {
        stage: 'result',
        userMessage: 'Lượt chạy cũ dùng model khác model đang cấu hình — hãy chạy lại từ đầu.',
        operatorHint: 'APM-H-ae: `ai_run.model` khác model đầu chuỗi hiện tại, và phần suy nghĩ của lượt cũ mang chữ ký của hãng cũ nên hãng mới từ chối. Bỏ khối `thinking` là đủ trong hầu hết trường hợp và harness tự làm; mã này chỉ nổi lên khi bỏ xong vẫn không còn lượt nào trọn vẹn. Đổi key hay đổi `ai.model.<tier>` giữa lúc run hỏng và lúc bấm "Chạy tiếp" là nguyên nhân thường gặp.',
    },
};
/** Stage của một mã lỗi; mã ngoài `AI_*` (FORBIDDEN, INTERNAL…) coi là `result`. */
export function stageOfError(code) {
    if (!code)
        return 'result';
    const entry = AI_ERROR_CATALOG[code];
    return entry?.stage ?? 'result';
}
/** Mọi mã `AI_*` đang có trong `ErrorCode` — để test và script sổ lỗi cùng đọc một danh sách. */
export const AI_ERROR_CODES = Object.keys(ErrorCode).filter((key) => key.startsWith('AI_'));
const STAGE_LABEL = {
    context: 'Context — dựng prompt',
    llm: 'LLM — gọi model',
    policy: 'Policy — cửa luật',
    runtime: 'Runtime — chạy tool, vòng lặp',
    verify: 'Verify — kiểm kết quả',
    result: 'Result — kết cục của run',
};
const STAGE_ORDER = ['context', 'llm', 'policy', 'runtime', 'verify', 'result'];
/**
 * `docs/ai-errors.md` — sinh **từ sổ lỗi**, không viết tay (`pnpm ai:errors:doc`).
 * Tất định: cùng code ⇒ cùng chuỗi, để CI so file với đầu ra và đỏ khi ai đó
 * sửa tay file .md hoặc thêm mã mà không chạy lại script.
 */
export function renderAiErrorsDoc() {
    const lines = [
        '# Sổ lỗi AI',
        '',
        '<!-- SINH TỰ ĐỘNG từ `packages/contracts/src/ai-errors.ts` bằng `pnpm ai:errors:doc`. Đừng sửa tay: CI so file này với code. -->',
        '',
        'Mỗi mã `AI_*` của [`api-conventions.md`](./api-conventions.md) khai ba điều ở `AI_ERROR_CATALOG` (APM-H-m, [`plan-harness.md` §9.2](./plan-harness.md)):',
        '',
        '- **Stage**: khối nào của harness hỏng — bước `error` trong dấu vết mang cùng `stage`, trang Harness tô cột theo nó.',
        '- **Người dùng thấy**: câu hiện ở giao diện. Không có chi tiết kỹ thuật; đường làm tay (`manualAlternative`) luôn hiện kèm.',
        '- **Người vận hành đọc**: bước nào trong dấu vết, nguyên nhân thường gặp, cách sửa. Message gốc của hạ tầng chỉ có trong log, tìm theo `runId` hoặc `logRef` (`runId:L<n>`) ghi trên bước.',
        '',
        'Thêm mã `AI_*` mà thiếu hàng ở sổ là test contracts đỏ (`ai-errors.spec.ts`).',
        '',
    ];
    for (const stage of STAGE_ORDER) {
        const codes = AI_ERROR_CODES.filter((code) => AI_ERROR_CATALOG[code].stage === stage).sort();
        if (codes.length === 0)
            continue;
        lines.push(`## ${STAGE_LABEL[stage]}`, '');
        for (const code of codes) {
            const entry = AI_ERROR_CATALOG[code];
            lines.push(`### \`${code}\``, '', `**Người dùng thấy:** ${entry.userMessage}`, '', `**Người vận hành đọc:** ${entry.operatorHint}`, '');
        }
    }
    return lines.join('\n');
}
//# sourceMappingURL=ai-errors.js.map